/**
 * "Process again" for an H2R file whose mapping is confirmed but whose
 * processing never finished. Starts it and goes to the processing screen with
 * the new run, as confirming the mapping does.
 */

import { useNavigate } from 'react-router-dom';
import { Button, Link as MuiLink } from '@mui/material';
import { useProcessAgainMutation } from '../../queries/intake';
import { useToast } from '../../provider/toast';
import { useModule } from '../modules/useModule';
import type { FileRecord } from '../../schemas';

export function ProcessAgain({
	file,
	variant = 'link',
}: {
	file: FileRecord;
	variant?: 'button' | 'link';
}) {
	const navigate = useNavigate();
	const { link } = useModule();
	const toast = useToast();
	const start = useProcessAgainMutation(file.pkFileId);

	const go = (event: React.MouseEvent) => {
		event.stopPropagation();
		start.mutate(undefined, {
			onSuccess: (run) => {
				toast('ok', 'Processing started again.');
				navigate(
					link(
						`/intake/clients/${file.fkClientId}/files/${file.pkFileId}/processing?run=${encodeURIComponent(run.normaliseRunId)}`,
					),
				);
			},
			onError: (error) =>
				toast(
					'danger',
					'Could not start processing',
					error instanceof Error ? error.message : undefined,
				),
		});
	};

	return variant === 'button' ? (
		<Button
			variant="contained"
			size="small"
			disabled={start.isPending}
			onClick={go}
		>
			Process again
		</Button>
	) : (
		<MuiLink
			component="button"
			type="button"
			underline="hover"
			disabled={start.isPending}
			onClick={go}
			sx={{ fontSize: 'var(--fs-small)', whiteSpace: 'nowrap' }}
		>
			Process again
		</MuiLink>
	);
}
